import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Stepper } from '@dreamtree-org/twreact-ui';
import { Download, LifeBuoy, RotateCcw, XCircle } from 'lucide-react';
import { KeyValue, Money, PageHeader, QueryState, SectionCard, StatusBadge } from '@/components';
import { useAppApi } from '@/lib/api';
import { useMutation, useQuery } from '@/lib/useQuery';
import { errorText, formatDate } from '@/lib/format';
import { cartTotals } from '../lib/cart';
import { timelineSteps } from '../lib/timeline';
import { useStorefront } from '../StorefrontContext';
import CartLineItem from '../components/CartLineItem';
import PriceSummary from '../components/PriceSummary';
import RequestDialog from '../components/RequestDialog';

const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const DIALOGS = {
  cancel: {
    title: 'Cancel this order',
    intro: 'The full amount is refunded to your original payment method within 5 working days.',
    topics: [{ value: 'changed_mind', label: 'Changed my mind' }, { value: 'better_price', label: 'Found a better price' }, { value: 'delivery_too_late', label: 'Delivery is too late' }, { value: 'other', label: 'Other' }],
    submit: 'Cancel order',
    requireMessage: false,
    destructive: true,
  },
  return: {
    title: 'Request a return',
    intro: 'Returns are accepted within 7 days of delivery when the serial and condition match the inspection passport. We pick it up from your address.',
    topics: [{ value: 'not_as_described', label: 'Not as described' }, { value: 'defective', label: 'Defective or not working' }, { value: 'changed_mind', label: 'Changed my mind' }],
    submit: 'Request return',
  },
  support: {
    title: 'Get help with this order',
    intro: 'The seller usually replies within 4 working hours.',
    topics: [{ value: 'delivery', label: 'Delivery' }, { value: 'product', label: 'Product question' }, { value: 'invoice', label: 'Invoice or GST' }, { value: 'refund', label: 'Refund' }, { value: 'other', label: 'Other' }],
    submit: 'Send request',
  },
};

const REQUEST_BADGE = { open: 'warning', approved: 'primary', resolved: 'success', rejected: 'error' };

/** WEB-07 order detail: canonical timeline, seller, masked serials, invoice, cancel/return/support. */
export default function OrderDetail() {
  const { id } = useParams();
  const sf = useStorefront();
  const { fetchApi } = useAppApi();
  const q = useQuery('sfOrder', { id, seller: sf.seller.name }, { select: (r) => r.data });
  const cancel = useMutation('sfOrderCancel');
  const request = useMutation('sfOrderRequest');
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  const downloadInvoice = async (kind) => {
    setInvoiceBusy(kind || 'sale');
    try {
      const r = await fetchApi('sfInvoice', { id, kind }, false);
      const url = URL.createObjectURL(new Blob([r.data.content], { type: r.data.mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.data.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      setNotice({ variant: 'error', text: errorText(ex) });
    } finally {
      setInvoiceBusy(false);
    }
  };

  const submit = async ({ topic, message }) => {
    try {
      if (dialog === 'cancel') {
        await cancel.run({ id, reason: topic, seller: sf.seller.name });
        setNotice({ variant: 'success', text: 'Order cancelled. Your refund has been initiated.' });
      } else {
        const r = await request.run({ id, kind: dialog, topic, message, seller: sf.seller.name });
        setNotice({ variant: 'success', text: `Request ${r.data.id} created. We'll update you by SMS.` });
      }
      setDialog(null);
      q.reload();
    } catch {
      /* error shown inside the dialog */
    }
  };

  const cfg = dialog && DIALOGS[dialog];

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(o) => {
          const { steps, currentStep } = timelineSteps(o.timeline, when, o.status);
          const totals = o.totals || cartTotals(o.items);
          return (
            <>
              <PageHeader
                title={`Order ${o.id}`}
                subtitle={`Placed ${formatDate(o.placed_at)} · sold by ${o.seller.name}`}
                back={{ label: 'My orders', href: '/account/orders' }}
                actions={<StatusBadge status={o.status} />}
              />
              {notice ? <Alert variant={notice.variant} dismissible onDismiss={() => setNotice(null)}>{notice.text}</Alert> : null}
              {o.refund ? <Alert variant="info" title="Refund initiated"><Money paise={o.refund.amount_paise} /> back to your {o.payment.method} by {formatDate(o.refund.eta)}.</Alert> : null}

              <div className="grid gap-5 lg:grid-cols-3">
                <SectionCard title="Order timeline">
                  <Stepper steps={steps} currentStep={currentStep} orientation="vertical" />
                  {o.shipment && o.shipment.awb ? (
                    <p className="text-sm opacity-80">{o.shipment.carrier} · AWB {o.shipment.awb}{o.shipment.eta ? ` · expected ${formatDate(o.shipment.eta)}` : ''}</p>
                  ) : null}
                </SectionCard>

                <div className="space-y-5 lg:col-span-2">
                  <SectionCard title="Items">
                    <div className="divide-y divide-[var(--app-border)]">
                      {o.items.map((i, n) => <CartLineItem key={n} item={i} compact />)}
                    </div>
                  </SectionCard>

                  <div className="grid gap-5 md:grid-cols-2">
                    <SectionCard title="Delivery and payment">
                      <KeyValue
                        items={[
                          { label: 'Deliver to', value: o.address },
                          { label: 'Payment', value: `${o.payment.method} · ${o.payment.status}` },
                          { label: 'Reference', value: o.payment.ref },
                          { label: 'Invoice', value: o.invoice_no },
                          o.credit_note_no ? { label: 'Credit note', value: o.credit_note_no } : null,
                          o.return_window_ends ? { label: 'Return window', value: o.can_return ? `Open until ${formatDate(o.return_window_ends)}` : (o.requests || []).some((r) => r.kind === 'return') ? 'Return requested' : `Closed ${formatDate(o.return_window_ends)}` } : null,
                        ]}
                      />
                    </SectionCard>
                    <SectionCard title="Price details">
                      <PriceSummary t={totals} />
                    </SectionCard>
                  </div>

                  <SectionCard title="Need something?">
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} loading={invoiceBusy === 'sale'} disabled={!o.invoice_no} onClick={() => downloadInvoice()}>Download invoice</Button>
                      {o.credit_note_no ? <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} loading={invoiceBusy === 'credit_note'} onClick={() => downloadInvoice('credit_note')}>Download credit note</Button> : null}
                      {o.can_cancel ? <Button variant="destructive" leftIcon={<XCircle className="h-4 w-4" />} onClick={() => setDialog('cancel')}>Cancel order</Button> : null}
                      {o.can_return ? <Button variant="outline" leftIcon={<RotateCcw className="h-4 w-4" />} onClick={() => setDialog('return')}>Request return</Button> : null}
                      <Button variant="outline" leftIcon={<LifeBuoy className="h-4 w-4" />} onClick={() => setDialog('support')}>Get help</Button>
                    </div>
                    {!o.can_cancel && !o.can_return && ['delivered', 'packed', 'shipped', 'out_for_delivery'].includes(o.status) && !(o.requests || []).some((r) => r.kind === 'return') ? (
                      <p className="text-xs opacity-70">{o.status === 'delivered' ? 'The return window has closed; warranty claims are open under Warranty.' : 'Shipped orders cannot be cancelled; you can return it within 7 days of delivery.'}</p>
                    ) : null}
                    {o.requests && o.requests.length ? (
                      <ul className="divide-y divide-[var(--app-border)] text-sm">
                        {o.requests.map((r) => (
                          <li key={r.id} className="py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span><strong>{r.id}</strong> · {r.kind === 'return' ? 'Return' : 'Support'} · {String(r.topic).replace(/_/g, ' ')}</span>
                              <Badge size="xs" variant={REQUEST_BADGE[r.status] || 'warning'}>{r.status}</Badge>
                            </div>
                            {r.resolution ? <p className="text-xs opacity-80 mt-1">{r.resolution}</p> : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </SectionCard>
                </div>
              </div>
            </>
          );
        }}
      </QueryState>
      {cfg ? (
        <RequestDialog
          isOpen
          onClose={() => setDialog(null)}
          title={cfg.title}
          intro={cfg.intro}
          topics={cfg.topics}
          submitLabel={cfg.submit}
          requireMessage={cfg.requireMessage !== false}
          destructive={cfg.destructive}
          loading={cancel.loading || request.loading}
          error={dialog === 'cancel' ? cancel.error : request.error}
          onSubmit={submit}
        />
      ) : null}
    </div>
  );
}
